import React, { useState, useEffect } from "react";
import { InfiniteLoader } from "react-virtualized";
import LoadingSpinner from "../../../atoms/loading-spinner";
import Explorer from "../../../../data-containers/explorer";
import URLState from "../../../../data-containers/url-state";
import DateRangeSelector from "./range-selector";
import LocationTable from "./table";
import LocationLabel from "./table-cell/label";
import LocationUsers from "./table-cell/users";
// import LocationTariff from "./table-cell/tariff";
import LocationInstallations from "./table-cell/installations";
import LocationConsumption from "./table-cell/consumption";
import LocationProduction from "./table-cell/production";
// import LocationDateModified from "./table-cell/date-modified";
import LocationGridOffset from "./table-cell/offset";
import LocationFleet from "./table-cell/fleet";
import TableHeaderWithIconContainer from "../../../atoms/TableHeaderWithIconContainer";
import ErrorBoundary from '../../../atoms/error-boundary';

export default function ActiveLocationsTable (props) {
  const {
    searchTerm,
    fetchNextActiveLocations,
    searchLoading,
    totalsLoading,
    getActiveLocations,
    selectedMapLocation,
  } = Explorer.useContainer();
  const { includeAllSubfleets } = URLState.useContainer();

  const [loadingNext, setLoadingNext] = useState(null);

  const [tableHeight, setTableHeight] = useState(0);
  const [tableInstance, setTableInstance] = useState({});
  // eslint-disable-next-line
  const [autoLoadNext, setAutoLoadNext] = useState(null);

  const locationsState = getActiveLocations();

  const loading = searchTerm ? searchLoading : totalsLoading;
  const rows = locationsState.results;
  const rowCount = locationsState.count;
  const next = locationsState.next;

  const generateColumns = ({ toggleExpand }) => {
    const columns = [
      {
        dataKey: "label",
        label: "ADDRESS",
        className: "column-label",
        headerClassName: "column-header-left-align-label",
        widthPercent: 0.2,
        cellRenderer: (props) => (
          <LocationLabel
            {...props}
            toggleExpand={toggleExpand}
            showDownload={true}
          />
        ),
        headerRenderer: ({ label }) => <div>{label}</div>,
      },
      // {
      //   label: "Date Modified",
      //   dataKey: "dt_modified",
      //   className: "date-modified",
      //   cellRenderer: (props) => <LocationDateModified {...props} />,
      // },
      {
        label: "RESIDENT",
        dataKey: "users",
        className: "column-users",
        headerClassName: "column-header-left-align-label",
        widthPercent: 0.2,
        cellRenderer: (props) => <LocationUsers {...props} />,
        headerRenderer: ({ label }) => <div>{label}</div>,
      },
      // {
      //   label: "Utility Provider",
      //   dataKey: "utility-provider",
      //   className: "column-tariff",
      //   cellRenderer: (props) => <LocationTariff {...props} />,
      // },
      {
        label: "PRODUCTION",
        dataKey: "production",
        className: "column-production",
        // widthPercent: 0.1,
        cellRenderer: (props) => <LocationProduction {...props} />,
        headerRenderer: ({ label }) => (
          <TableHeaderWithIconContainer label={label} />
        ),
      },
      {
        label: "CONSUMPTION",
        dataKey: "consumption",
        className: "column-consumption",
        // widthPercent: 0.08,
        cellRenderer: (props) => <LocationConsumption {...props} />,
        headerRenderer: ({ label }) => (
          <TableHeaderWithIconContainer label={label} />
        ),
      },

      {
        label: "ENERGY OFFSET",
        dataKey: "grid-offset",
        className: "column-offset",
        // widthPercent: 0.07,
        cellRenderer: (props) => <LocationGridOffset {...props} />,
        headerRenderer: ({ label }) => (
          <TableHeaderWithIconContainer label={label} />
        ),
      },
      {
        label: "HUB STATUS",
        dataKey: "installations",
        className: "column-installations",
        cellRenderer: (props) => <LocationInstallations {...props} />,
        headerRenderer: ({ label }) => (
          <TableHeaderWithIconContainer label={label} />
        ),
      },
      {
        label: "HVAC",
        dataKey: "consumption",
        className: "column-consumption",
        // widthPercent: 0.08,
        cellRenderer: (props) => (
          <div className="na">—</div>
        ),
        headerRenderer: ({ label }) => (
          <div style={{ opacity: ".3" }}>
            <TableHeaderWithIconContainer label={label} />
          </div>
        ),
      },
    ];

    if(includeAllSubfleets){
      columns.splice(
        1,
        0,
        {
          label: 'FLEET',
          dataKey: 'fleet',
          cellRenderer: props => <LocationFleet {...props} />
        }
      );
    }

    return columns;
  };

  const viewportIsFull = () => {
    if (
      !tableInstance._cache ||
      typeof tableInstance._cache.rowHeight !== "function"
    ) {
      return true;
    }

    let contentHeight = 0;
    let i = 0;
    while (i < rows.length) {
      let rowHeight = 0;
      try {
        rowHeight = tableInstance._cache.rowHeight({ index: i });
      } catch (err) {
        //
      }
      contentHeight += rowHeight;
      i++;
      if (contentHeight > tableHeight) {
        return true;
      }
    }
    return false;
  };

  const passiveLoadNext = async () => {
    if (locationsState.loading) {
      return;
    }
    setLoadingNext(true);
    await fetchNextActiveLocations();
    setLoadingNext(false);
  };

  useEffect(() => {
    if (next && !viewportIsFull() && !locationsState.loading) {
      passiveLoadNext();
    }
  }, [next, tableHeight, locationsState.loading]);

  useEffect(() => {
    if (autoLoadNext) {
      passiveLoadNext();
    }
  }, [autoLoadNext]);

  return <div className="explorer-locations explorer-locations-active">
    {loading && <LoadingSpinner className="inline explorer-table-loading" />}
    <DateRangeSelector />
    <InfiniteLoader
      isRowLoaded={({ index }) => !!rows[index]}
      loadMoreRows={passiveLoadNext}
      rowCount={rowCount || 0}
      threshold={1}
    >
      {({ onRowsRendered, registerChild }) => (
        <LocationTable
          id="locations-table"
          generateColumns={generateColumns}
          rows={rows}
          loading={loading}
          onRowsRendered={onRowsRendered}
          ref={registerChild}
          setTableHeight={setTableHeight}
          setTableInstance={setTableInstance}
          selectedMapLocation={selectedMapLocation}
          {...props}
        />
      )}
    </InfiniteLoader>
    {loadingNext && (
      <div className="explorer-next-loading">
        <LoadingSpinner className="inline tiny" />
        &nbsp; Loading more rows&hellip;
      </div>
    )}
  </div>;
}
